import React from 'react';
import { AlertTriangle, DollarSign } from 'lucide-react';

const Disclaimer = () => {
  return (
    <div className="py-20 animate-fade-in">
      <div className="container-custom">
        <div className="max-w-4xl mx-auto">
          {/* Header */}
          <div className="text-center mb-12">
            <div className="inline-flex p-4 glass-card rounded-2xl mb-6">
              <AlertTriangle className="w-12 h-12 text-warning" />
            </div>
            <h1 className="text-4xl lg:text-5xl font-bold mb-4">
              <span className="gradient-text">Disclaimer</span>
            </h1>
            <p className="text-muted-foreground">
              Last Updated: January 1, 2024
            </p>
          </div>

          {/* Content */}
          <div className="glass-card p-8 lg:p-12 space-y-8">
            {/* Advertising Disclaimer - CUSTOM */}
            <section className="glass p-6 rounded-lg border border-warning/30 bg-warning/5">
              <div className="flex items-start space-x-4">
                <DollarSign className="w-8 h-8 text-warning flex-shrink-0 mt-1" />
                <div>
                  <h2 className="text-2xl font-bold mb-4 text-warning">About Our Advertisements</h2>
                  <p className="text-foreground leading-relaxed mb-4">
                    <span className="font-semibold">Important:</span> We display advertisements on
                    our website through Google AdSense.
                  </p>
                  <p className="text-foreground leading-relaxed mb-4 font-semibold">
                    Showing ads does NOT mean we collect or track your personal data.
                  </p>
                  <p className="text-muted-foreground leading-relaxed mb-4">
                    Advertising is the only way we earn revenue to maintain this free service.
                    However, ad networks operate independently of our tool functionality.
                  </p>
                  <div className="glass p-4 rounded-lg">
                    <p className="text-sm text-muted-foreground">
                      <span className="font-semibold text-foreground">What this means:</span>
                    </p>
                    <ul className="mt-2 space-y-2 text-sm text-muted-foreground">
                      <li className="flex items-start">
                        <span className="text-success mr-2">✓</span>
                        We never access data you process in our tools
                      </li>
                      <li className="flex items-start">
                        <span className="text-success mr-2">✓</span>
                        We never store files, passwords, or encryption keys
                      </li>
                      <li className="flex items-start">
                        <span className="text-success mr-2">✓</span>
                        We never share your information with advertisers
                      </li>
                      <li className="flex items-start">
                        <span className="text-success mr-2">✓</span>
                        Our tools work 100% offline after the page loads
                      </li>
                    </ul>
                  </div>
                  <p className="text-muted-foreground leading-relaxed mt-4 text-sm">
                    Third-party ad networks may use cookies for personalized advertising. You can
                    opt out anytime through your browser settings or ad network preferences. See our
                    Privacy Policy for more details.
                  </p>
                </div>
              </div>
            </section>

            {/* General Disclaimer */}
            <section>
              <h2 className="text-2xl font-bold mb-4">General Disclaimer</h2>
              <p className="text-muted-foreground leading-relaxed">
                The information and tools provided on NoTrackTools are for general informational and
                educational purposes only. While we strive to provide accurate and up-to-date tools,
                we make no representations or warranties of any kind, express or implied, about the
                completeness, accuracy, reliability, suitability, or availability of the Service.
              </p>
            </section>

            {/* No Professional Advice */}
            <section>
              <h2 className="text-2xl font-bold mb-4">No Professional Advice</h2>
              <p className="text-muted-foreground leading-relaxed">
                The tools and information on this website are not intended to be a substitute for
                professional security advice, legal advice, or technical consultation. Always seek
                the advice of qualified professionals regarding your specific security needs.
              </p>
            </section>

            {/* Use at Your Own Risk */}
            <section>
              <h2 className="text-2xl font-bold mb-4">Use at Your Own Risk</h2>
              <p className="text-muted-foreground leading-relaxed mb-4">
                Any reliance you place on our tools or information is strictly at your own risk. We
                are not liable for:
              </p>
              <ul className="space-y-2 text-muted-foreground">
                <li className="flex items-start">
                  <span className="mr-2">•</span>
                  Loss of data due to forgotten passwords or encryption keys
                </li>
                <li className="flex items-start">
                  <span className="mr-2">•</span>
                  Misuse of encrypted or encoded content
                </li>
                <li className="flex items-start">
                  <span className="mr-2">•</span>
                  Technical errors or browser compatibility issues
                </li>
                <li className="flex items-start">
                  <span className="mr-2">•</span>
                  Any damages arising from your use of the Service
                </li>
              </ul>
            </section>

            {/* No Warranty */}
            <section>
              <h2 className="text-2xl font-bold mb-4">No Warranty</h2>
              <p className="text-muted-foreground leading-relaxed">
                The Service is provided “as is” without any warranties, express or implied. We do
                not warrant that the Service will be uninterrupted, secure, or error-free, or that
                defects will be corrected.
              </p>
            </section>

            {/* External Links */}
            <section>
              <h2 className="text-2xl font-bold mb-4">External Links</h2>
              <p className="text-muted-foreground leading-relaxed">
                Our Service may contain links to external websites that are not operated by us. We
                have no control over the content and practices of these sites and cannot accept
                responsibility for their privacy policies or content.
              </p>
            </section>

            {/* Encryption Disclaimer */}
            <section>
              <h2 className="text-2xl font-bold mb-4">Encryption and Security</h2>
              <p className="text-muted-foreground leading-relaxed">
                While we use industry-standard encryption algorithms, no method of electronic
                storage or transmission is 100% secure. We cannot guarantee absolute security of
                your data. You are responsible for choosing strong passwords and keeping them
                confidential.
              </p>
            </section>

            {/* Legal Compliance */}
            <section>
              <h2 className="text-2xl font-bold mb-4">Legal Compliance</h2>
              <p className="text-muted-foreground leading-relaxed">
                You are responsible for ensuring that your use of our tools complies with all
                applicable laws and regulations in your jurisdiction. We do not endorse or condone
                the use of our tools for illegal activities.
              </p>
            </section>

            {/* Changes */}
            <section>
              <h2 className="text-2xl font-bold mb-4">Changes to Disclaimer</h2>
              <p className="text-muted-foreground leading-relaxed">
                We may update this Disclaimer from time to time. Changes will be posted on this page
                with an updated “Last Updated” date. Your continued use of the Service after any
                changes constitutes acceptance of the new Disclaimer.
              </p>
            </section>

            {/* Contact */}
            <section>
              <h2 className="text-2xl font-bold mb-4">Questions</h2>
              <p className="text-muted-foreground leading-relaxed">
                If you have any questions about this Disclaimer, please contact us at:
                <a
                  href="mailto:praneethartha381@gmail.com"
                  className="block mt-2 text-primary hover:underline"
                >
                  praneethartha381@gmail.com
                </a>
              </p>
            </section>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Disclaimer;
